<script lang="ts">
	import { format } from 'svelte-i18n';
	import {
		Alert,
		Button,
		Container,
		Modal,
		Radio,
		Select,
		Text,
		toast,
	} from '@Shelly/shelly-design-system/index';
	import { real_id } from 'src/helpers/interface_helpers';
	import { BtHomeController } from 'src/helpers/virtual_helpers/ble_control_helpers';
	import { getBthomeDeviceModel } from 'src/helpers/virtual_helpers/virtual_bthome_helper';
	import {
		getDeviceSettings,
		getDeviceStatus,
		readDeviceSettings,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	import { btHomeDiscoveredDevices } from 'src/stores/features/ble/bt-home-discovered';
	import {
		buildBTHomeLearningParams,
		isBTHomeLearnInProgressMessage,
		isBTHomeLearnInProgressRpcError,
	} from 'src/helpers/bthome_learning_helper';
	import type { SelectOptionPropsType } from '@Shelly/shelly-design-system';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';

	interface DiscoveredDevice {
		addr: string;
		encrypted: boolean;
		local_name: string;
		rssi: number;
		shelly_mfdata: {
			model_id: number;
			flags: number;
			mac: string;
		};
	}
	import { createEventDispatcher, onDestroy, onMount } from 'svelte';

	export let device: Device;
	export let pairedDevices: string[] = [];
	export let pairedDeviceLabels: Record<string, string> = {};
	export let pairedDeviceChannelLabels: Record<string, Record<string, string>> = {};

	const deviceID = real_id(device.id);
	$: settingsStore = getDeviceSettings(deviceID);
	$: settings = $settingsStore;
	const dispatch = createEventDispatcher();

	let loading = {};
	let errorMessage = '';
	let showPairingModal = false;
	let selectedPairingMode: PairingMode = 'new';
	let selectedPairedDevice = '';
	let selectedPairedDeviceOption: SelectOptionPropsType | null = null;
	let selectedDiscoveredDevice: DiscoveredDevice | null = null;
	let selectedDiscoveredDeviceOption: SelectOptionPropsType | null = null;
	let selectedOutput;
	let showDiscoveryReminder = false;
	let clearTimeoutId: ReturnType<typeof setTimeout> | null = null;

	const PAIRING_MODE_NEW = 'new';
	const PAIRING_MODE_EXISTING = 'existing';
	const REQUIRED_MODEL_ID = 9;

	type PairingMode = typeof PAIRING_MODE_NEW | typeof PAIRING_MODE_EXISTING;

	$: learningInProgress = $btHomeDiscoveredDevices?.[deviceID]?.runningDiscovery;

	$: discoveryError = $btHomeDiscoveredDevices?.[deviceID]?.error;
	$: discoveredDevices = $btHomeDiscoveredDevices?.[deviceID]?.discoveredDevices || [];
	$: discoveryStatus = $btHomeDiscoveredDevices?.[deviceID]?.status || 'idle';
	$: componentAdded = $btHomeDiscoveredDevices?.[deviceID]?.componentAdded;

	$: discoveredDeviceOptions = discoveredDevices.map((device, index) => ({
		title: device.local_name || device.addr || `Device ${index + 1}`,
		value: device.addr,
	}));

	$: hasDiscoveredDevices = discoveredDevices.length > 0 && learningInProgress;

	const outputs = Object.keys(settings).reduce((acc, key) => {
		const [, channelStr] = key.split(':');
		const channelIndex = Number(channelStr);
		if (
			Number.isFinite(channelIndex) &&
			channelIndex < 100 &&
			!/input|script/.test(key) &&
			(typeof device?.channel !== 'number' || device.channel === channelIndex)
		) {
			acc.push({
				id: channelStr,
				name: key,
			});
			loading[key] = false;
		}
		return acc;
	}, []);

	$: controlRemoteIds = pairedDevices.filter((remoteId) => remoteId.startsWith('bthomecontrol:'));
	$: selectableRemoteIds = (() => {
		if (controlRemoteIds.length > 0) {
			return controlRemoteIds;
		}
		return pairedDevices;
	})();
	$: hasPairedDevices = selectableRemoteIds.length > 0;
	$: pairedDeviceOptions = (() => {
		const statusMap = (readDeviceStatus(deviceID) ?? {}) as Record<string, any>;
		const settingsMap = (readDeviceSettings(deviceID) ?? {}) as Record<string, any>;
		return selectableRemoteIds.map<SelectOptionPropsType>((remoteId) => {
			const remoteStatusRecord = statusMap?.[remoteId] as Record<string, any> | undefined;
			const remoteStatus = remoteStatusRecord as any;
			const model = getBthomeDeviceModel(remoteStatus);
			const modelId = model?.id ?? remoteStatusRecord?._attrs?.model_id;
			const settingsModelId = settingsMap?.[remoteId]?._attrs?.model_id;
			const finalModelId = modelId ?? settingsModelId;
			const isDisabled = finalModelId !== undefined && finalModelId !== REQUIRED_MODEL_ID;
			return {
				title: pairedDeviceLabels?.[remoteId] || remoteId,
				value: remoteId,
				disabled: isDisabled,
			};
		});
	})();

	$: if (selectedPairingMode === PAIRING_MODE_EXISTING && pairedDevices.length === 0) {
		selectedPairingMode = PAIRING_MODE_NEW;
	}

	$: if (selectedPairingMode === PAIRING_MODE_EXISTING && hasPairedDevices && !selectedPairedDevice) {
		const defaultOption =
			pairedDeviceOptions.find((opt) => !opt.disabled) || pairedDeviceOptions?.[0];
		selectedPairedDeviceOption = defaultOption || null;
		selectedPairedDevice = (defaultOption?.value as string) || '';
	}

	$: if (
		selectedPairingMode === PAIRING_MODE_EXISTING &&
		selectedPairedDeviceOption &&
		selectedPairedDeviceOption.disabled
	) {
		const enabledOption = pairedDeviceOptions.find((opt) => !opt.disabled);
		selectedPairedDeviceOption = enabledOption || null;
		selectedPairedDevice = (enabledOption?.value as string) || '';
	}

	$: if (
		selectedPairingMode === PAIRING_MODE_EXISTING &&
		hasPairedDevices &&
		selectedPairedDeviceOption &&
		!pairedDeviceOptions.some((option) => option.value === selectedPairedDeviceOption?.value)
	) {
		const defaultOption = pairedDeviceOptions?.[0];
		selectedPairedDeviceOption = defaultOption || null;
		selectedPairedDevice = (defaultOption?.value as string) || '';
	}

	$: if (!hasPairedDevices) {
		selectedPairedDeviceOption = null;
		selectedPairedDevice = '';
	}

	/**
	 * Resets the pairing modal state to defaults.
	 */
	const resetPairingModalState = () => {
		showPairingModal = false;
		selectedOutput = undefined;
		selectedPairingMode = PAIRING_MODE_NEW;
		const defaultOption = pairedDeviceOptions?.[0];
		selectedPairedDeviceOption = defaultOption || null;
		selectedPairedDevice = (defaultOption?.value as string) || '';
		selectedDiscoveredDevice = null;
		selectedDiscoveredDeviceOption = null;
		errorMessage = '';
	};

	/**
	 * Opens the pairing modal for a specific output.
	 *
	 * @param {Object} output - Output component to pair with
	 */
	const openPairingModal = (output) => {
		resetPairingModalState();
		selectedOutput = output;
		showPairingModal = true;
	};

	/**
	 * Handles selection change for existing remote dropdown.
	 *
	 * @param {SelectOptionPropsType | null} option - Selected option
	 */
	/**
	 * Handles selection change for existing remote dropdown.
	 * Prevents selecting disabled options and updates the selected device state.
	 *
	 * @param {SelectOptionPropsType | null} option - Selected option
	 */
	const handleExistingRemoteChange = (option: SelectOptionPropsType | null) => {
		if (option?.disabled) {
			if (!selectedPairedDeviceOption || selectedPairedDeviceOption.disabled) {
				selectedPairedDeviceOption = null;
				selectedPairedDevice = '';
			}
			return;
		}

		selectedPairedDeviceOption = option;
		const optionValue = option?.value;

		if (typeof optionValue === 'string') {
			selectedPairedDevice = optionValue;
			return;
		}

		if (!optionValue) {
			selectedPairedDevice = '';
			return;
		}

		selectedPairedDevice = String(optionValue);
	};

	/**
	 * Handles select change event for existing remote dropdown.
	 *
	 * @param {CustomEvent<SelectOptionPropsType | null>} event - Select change event
	 */
	const handleExistingRemoteSelectChange = (event: CustomEvent<SelectOptionPropsType | null>) => {
		handleExistingRemoteChange(event.detail || null);
	};

	/**
	 * Handles selection change for discovered device dropdown.
	 *
	 * @param {SelectOptionPropsType | null} option - Selected option
	 */
	const handleDiscoveredDeviceChange = (option: SelectOptionPropsType | null) => {
		selectedDiscoveredDeviceOption = option;
		if (option && typeof option.value === 'string') {
			const device = discoveredDevices.find((d) => d.addr === option.value);
			selectedDiscoveredDevice = device || null;
		} else {
			selectedDiscoveredDevice = null;
		}
	};

	/**
	 * Handles select change event for discovered device dropdown.
	 *
	 * @param {CustomEvent<SelectOptionPropsType | null>} event - Select change event
	 */
	const handleDiscoveredDeviceSelectChange = (event: CustomEvent<SelectOptionPropsType | null>) => {
		handleDiscoveredDeviceChange(event.detail || null);
	};

	/**
	 * Starts or stops BTHome device learning mode.
	 * If learning is already in progress, stops it. Otherwise, starts learning for the specified output component.
	 * Handles the "Learn in progress" error (-114) by enabling the stop button instead of showing an error.
	 *
	 * @param {Object} input - The output component to start learning for (e.g., { id: "0", name: "rgbcct:0" })
	 * @returns {Promise<void>}
	 *
	 * @example
	 * startLearning({ id: "0", name: "rgbcct:0" }) // Starts learning for rgbcct:0
	 * startLearning({ id: "0", name: "cover:0" }) // Starts learning for cover:0
	 */
	const startLearning = async (input) => {
		try {
			btHomeDiscoveredDevices.initDiscovery(deviceID);
			loading = { [input.id || input.name]: true };

			if (learningInProgress) {
				const res: SettingRequest = await BtHomeController.getInstance().stopLearning(device);
				if (res?.isok) {
					btHomeDiscoveredDevices.stopDiscovery(deviceID);
				} else {
					toast.open({ type: 'danger', title: $format('error_global') });
					return;
				}
				return;
			}

			const params = buildBTHomeLearningParams(input);

			const res: SettingRequest = await BtHomeController.getInstance().startLearning(
				device,
				params,
			);

			if (res?.isok) {
				showDiscoveryReminder = true;
				setTimeout(async () => {
					const discoveryState = $btHomeDiscoveredDevices?.[deviceID];
					if (discoveryState?.runningDiscovery) {
						btHomeDiscoveredDevices.setTimeout(deviceID);
						const stopRes: SettingRequest =
							await BtHomeController.getInstance().stopLearning(device);
						if (stopRes?.isok) {
							btHomeDiscoveredDevices.stopDiscovery(deviceID);
						}
					}
				}, 30000);
			} else {
				const rawError = res?.errors?.message ?? res?.error ?? res?.errors ?? res?.data?.error;
				const deviceError =
					(res as any)?.device_error ||
					(res?.errors as any)?.device_error ||
					(res?.data as any)?.device_error;

				if (isBTHomeLearnInProgressRpcError(rawError, deviceError)) {
					btHomeDiscoveredDevices.initDiscovery(deviceID);
					return;
				}

				toast.open({ type: 'danger', title: $format('error_global') });
				return;
			}
		} catch (error) {
			let errorStr: string;
			if (typeof error === 'string') {
				errorStr = error;
			} else if (error instanceof Error) {
				errorStr = error.message;
			} else {
				errorStr = JSON.stringify(error);
			}

			if (isBTHomeLearnInProgressMessage(errorStr)) {
				btHomeDiscoveredDevices.initDiscovery(deviceID);
				return;
			}

			toast.open({ type: 'danger', title: $format('error_global') });
			btHomeDiscoveredDevices.setError(deviceID, $format('error_global'));
		} finally {
			loading = { [input.id || input.name]: false };
		}
	};

	const handlePairingConfirm = async () => {
		//To Do: I need to check if we can display discovered devices!!! This was possible back in the day when this process started.
		if (!selectedOutput) {
			return;
		}

		const output = selectedOutput;
		const mode = selectedPairingMode;
		const remote = selectedPairedDevice;

		resetPairingModalState();

		if (mode === PAIRING_MODE_EXISTING && remote) {
			dispatch('useExistingRemote', {
				output,
				remote,
				channelLabels: pairedDeviceChannelLabels?.[remote] || {},
			});
			return;
		}

		if (mode === PAIRING_MODE_NEW && selectedDiscoveredDevice) {
			dispatch('useDiscoveredDevice', {
				output,
				device: selectedDiscoveredDevice,
			});
			return;
		}

		await startLearning(output);
	};

	let previousLearningState = false;
	let previousComponentAdded = false;
	let previousDiscoveryStatus: string | undefined;
	$: {
		const wasLearning = previousLearningState;
		const isLearning = learningInProgress;

		if (wasLearning && !isLearning) {
			dispatch('learningCompleted');
			showDiscoveryReminder = false;
		}

		previousLearningState = isLearning;
	}

	$: {
		if (!previousComponentAdded && componentAdded) {
			dispatch('learningCompleted');
		}
		previousComponentAdded = componentAdded;
	}

	$: {
		const currentStatus = discoveryStatus;
		const wasDiscovering = previousDiscoveryStatus === 'discovering';
		const isTimeoutOrStopped = currentStatus === 'timeout' || currentStatus === 'stopped';

		if (wasDiscovering && isTimeoutOrStopped) {
			dispatch('learningCompleted');
		}

		previousDiscoveryStatus = currentStatus;
	}

	$: if (discoveryError) {
		toast.open({ type: 'danger', title: $format('error_global') });
	}

	$: {
		if (discoveryStatus === 'timeout' && !learningInProgress) {
			if (clearTimeoutId) {
				clearTimeout(clearTimeoutId);
			}
			clearTimeoutId = setTimeout(() => {
				if (
					$btHomeDiscoveredDevices?.[deviceID]?.status === 'timeout' &&
					!$btHomeDiscoveredDevices?.[deviceID]?.runningDiscovery
				) {
					btHomeDiscoveredDevices.clearErrors(deviceID);
				}
				clearTimeoutId = null;
			}, 2000);
		}
	}

	onMount(() => {
		// Only clear errors if there's a stale timeout/error state, not on every mount
		// This prevents clearing errors when the component remounts after a successful list fetch
		const currentState = $btHomeDiscoveredDevices?.[deviceID];
		if (currentState?.status === 'timeout' || currentState?.status === 'error') {
			btHomeDiscoveredDevices.clearErrors(deviceID);
		}
	});

	onDestroy(() => {
		if (clearTimeoutId) {
			clearTimeout(clearTimeoutId);
		}
		btHomeDiscoveredDevices.clearErrors(deviceID);
		btHomeDiscoveredDevices.stopDiscovery(deviceID);
		btHomeDiscoveredDevices.clearDevices(deviceID);
	});
</script>

{#if discoveryStatus === 'timeout'}
	<Alert
		type="warning"
		class="mb-s"
		heading={$format('discovery_timeout', {
			default: 'Discovery timed out. Please try again.',
		})}
	/>
	<!-- {:else if discoveryStatus === 'success' && discoveredDevices.length > 0 && !componentAdded}
	<Alert
		type="success"
		class="mb-s"
		heading={$format('devices_discovered', {
			default:
				discoveredDevices.length === 1
					? 'Discovered 1 device'
					: `Discovered ${discoveredDevices.length} devices`,
		})}
	/> -->
	<!-- Will remove this commented code. Just I am not sure if we need to show this alert. -->
{:else if learningInProgress}
	{#if showDiscoveryReminder}
		<Alert
			type="info"
			class="mb-s"
			heading={$format('alert_title_pair_remote_controls_description', {
				default:
					'Enable the discovery mode of your Shelly BLU device as described in the user manual.',
			})}
		/>
	{/if}
	<Alert
		type="info"
		class="mb-s"
		heading={$format('discovery_in_progress', {
			default: 'Discovery in progress... Please wait.',
		})}
	/>
{/if}
{#each outputs as output}
	<Container class="mb-s" padding="s">
		<div class="d-flex justify-content-between align-items-center gap-s flex-wrap">
			<Text styleType="body-small" class="text-capitalize text-break-all">
				{#if output.name && typeof output.name === 'string'}
					{@const [componentType, channelStr] = output.name.split(':')}
					{@const channelIndex = Number(channelStr ?? '0')}
					{#if componentType === 'cover'}
						{$format('label_cover', { default: 'cover' })} {channelIndex}:
					{:else if componentType === 'switch'}
						{$format('label_switch', { default: 'switch' })} {channelIndex}:
					{:else}
						{output.name}:
					{/if}
				{:else}
					{output.name}:
				{/if}
			</Text>
			<Button
				class="align-self-end"
				size="m"
				on:click={() => (learningInProgress ? startLearning(output) : openPairingModal(output))}
				loading={loading[output.id]}
				>{learningInProgress
					? $format('button_stop_pairing', { default: 'Stop pairing' })
					: $format('button_start_pairing', {
							default: 'Start pairing',
						})}</Button
			>
		</div>
	</Container>
{/each}

<Modal
	showModal={showPairingModal}
	on:close={resetPairingModalState}
	title={$format('modal_title_add_remote_control', {
		default: 'Add remote control',
	})}
>
	<div slot="content" class="d-flex flex-direction-column gap-s">
		<Radio
			size="m"
			bind:selected={selectedPairingMode}
			value={PAIRING_MODE_NEW}
			id="pairing-mode-new"
		>
			{$format('radio_label_find_new_remote', { default: 'Find a new remote' })}
		</Radio>
		<Radio
			size="m"
			bind:selected={selectedPairingMode}
			value={PAIRING_MODE_EXISTING}
			id="pairing-mode-existing"
			disabled={!hasPairedDevices}
		>
			{$format('radio_label_use_paired_device', {
				default: 'Use paired bluetooth device',
			})}
		</Radio>
		{#if selectedPairingMode === PAIRING_MODE_NEW && hasDiscoveredDevices}
			<Select
				id="discovered-device-select"
				icon={null}
				aria-label={$format('select_discovered_device_label', {
					default: 'Select discovered device',
				})}
				label={$format('select_discovered_device_label', {
					default: 'Select discovered device',
				})}
				options={discoveredDeviceOptions}
				value={selectedDiscoveredDeviceOption}
				on:change={handleDiscoveredDeviceSelectChange}
			/>
		{/if}
		{#if selectedPairingMode === PAIRING_MODE_EXISTING}
			<Select
				id="existing-remote-select"
				icon={null}
				aria-label={$format('select_existing_remote_label', {
					default: 'Select remote',
				})}
				label={$format('select_existing_remote_label', {
					default: 'Select remote',
				})}
				options={pairedDeviceOptions}
				value={selectedPairedDeviceOption}
				on:change={handleExistingRemoteSelectChange}
			/>
		{/if}
	</div>

	<div slot="footer" class="d-flex justify-content-end gap-s">
		<Button
			variant="secondary"
			on:click={resetPairingModalState}
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			variant="primary"
			on:click={handlePairingConfirm}
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			disabled={selectedPairingMode === PAIRING_MODE_EXISTING &&
				(!selectedPairedDevice ||
					selectedPairedDeviceOption?.disabled ||
					pairedDeviceOptions.length === 0)}
		>
			{$format('button_start_pairing', { default: 'Start pairing' })}
		</Button>
	</div>
</Modal>
